// Wohler Chemical — Mitragynine & MGM Analogs (Program A/D)

export const mitragynineCompounds = {
  "mgm-9": {
    id: "mgm-9",
    name: "MGM-9",
    fullName: "7,9-Difluoromitragynine (10-fluoro-mitragynine pseudoindoxyl scaffold variant)",
    aliases: ["Difluoromitragynine (Wohler internal)"],
    categoryId: "mitragynine",
    program: "Program A / D",
    formula: "C₂₃H₂₉FN₂O₄ (per proposed structure)",
    molecularWeight: "≈ 420 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled as of 2026. Mitragynine itself is not federally scheduled. State scheduling varies significantly for kratom/mitragynine compounds — verify per state. Given the high MOR potency of MGM-9 and its structural relationship to scheduled opioids, this compound is flagged Very High analog/enforcement concern. University-institutional research-only posture is mandatory.",
    benefitScore: 86,
    harmScore: 48,
    evidenceConfidence: "MODERATE",
    status: "Hold — Institutional Only",
    statusColor: "orange",
    goDecision: "HOLD — pending legal memo and university/CRO partner identification",
    summary: "MGM-9 is a fluorinated mitragynine derivative generated via a Wohler Chemical one-pot oxidative/fluorination process using PIFA and Selectfluor. Preliminary pharmacology indicates potent mu-opioid receptor (MOR) activity with kappa-opioid receptor (KOR) involvement. Designed as a research probe for biased-opioid receptor pharmacology and fentanyl-harm-reduction research. Wohler internal process development has achieved ≈70 % purity at 10-g scale; the current program is finalizing chromatographic separation and crystallization.",
    pros: [
      "Potent opioid-receptor ligand with kratom-scaffold origin — clean 'natural product research' narrative",
      "Wohler Chemical has proprietary process knowledge (one-pot PIFA/Selectfluor protocol)",
      "Relevant to fentanyl-harm-reduction research: G-protein biased signaling hypothesis",
      "Scalable synthesis validated through 10-g campaign (Acosta & Yeh, 2026)",
      "Strong university research value for biased-MOR pharmacology programs"
    ],
    cons: [
      "High potency at MOR → enforcement scrutiny and diversion risk",
      "Fluorinated opioid analog → emergency scheduling risk similar to 2-FDCK (scheduled Jan 2026)",
      "Requires tight institutional control — not a general research-use compound",
      "PIFA (iodine(III) oxidant) is a thermal-shock-sensitive reagent — kg scale requires engineering controls",
      "Selectfluor requires dry/controlled conditions; exotherm management critical"
    ],
    pharmacology: {
      targets: [
        { receptor: "μ-opioid receptor (MOR)", activity: "Potent agonist; biased G-protein signaling (hypothesized)", value: "Low-nM affinity (preliminary)" },
        { receptor: "κ-opioid receptor (KOR)", activity: "Partial agonist/modulator", value: "Under characterization" },
        { receptor: "δ-opioid receptor (DOR)", activity: "Weak", value: "Minimal" }
      ],
      duration: "Estimated longer than mitragynine due to fluorine-enhanced metabolic stability",
      metabolism: "CYP3A4 N-demethylation (analogous to mitragynine); fluorine at C-7/C-9 blocks aromatic hydroxylation",
      mechanism: "Proposed biased MOR agonist (G-protein > β-arrestin), analogous to mitragynine pseudoindoxyl but with improved PK via fluorination"
    },
    labSynthesis: {
      title: "Laboratory One-Pot Synthesis (10 g) — Wohler Internal Protocol",
      overview: "One-pot, two-step: (1) PIFA-mediated oxidation of mitragynine in ACN/ethylene glycol at −5 °C, followed by (2) Selectfluor-driven fluorination at 15 °C in the same flask. Aqueous workup and EtOAc extraction delivers MGM-9 at ≈ 70 % LCMS purity, amenable to column chromatography purification.",
      steps: [
        {
          step: "1",
          title: "Reactor preparation & charging",
          description: "Charge 10 g mitragynine oxalate (1.0 eq, ≈ 20 mmol) into a jacketed reactor with 50 mL acetonitrile + 50 mL ethylene glycol. Stir, purge with N₂, cool to −5 °C.",
          solvent: "Acetonitrile (ACN) + Ethylene glycol (1:1)",
          keyReagents: "Mitragynine oxalate"
        },
        {
          step: "2",
          title: "PIFA addition (oxidative step)",
          description: "Add PIFA [PhI(OAc)(OCOCF₃) / phenyliodine(III) bis(trifluoroacetate)] (9.7 g, 1.1 eq) in portions over 10 min, maintaining T ≤ 0 °C. Stir 20 min. Intermediate: indoxyl/iminium species.",
          solvent: "ACN/ethylene glycol",
          keyReagents: "PIFA (1.1 eq)"
        },
        {
          step: "3",
          title: "Selectfluor addition (fluorination step)",
          description: "Warm to 15 °C. Add Selectfluor™ [1-chloromethyl-4-fluoro-1,4-diazoniabicyclo[2.2.2]octane bis(tetrafluoroborate)] (8.72 g, 1.2 eq) in portions. Maintain 15 °C; stir 45 min. LCMS monitoring for MGM-9 peak.",
          solvent: "ACN/ethylene glycol",
          keyReagents: "Selectfluor (1.2 eq)"
        },
        {
          step: "4",
          title: "Aqueous workup",
          description: "Quench with saturated NaHCO₃ (50 mL). Extract with EtOAc (3 × 75 mL). Combine organics, wash with brine (50 mL), dry over Na₂SO₄, concentrate.",
          solvent: "EtOAc / sat. NaHCO₃ / brine",
          keyReagents: "NaHCO₃, brine, Na₂SO₄"
        },
        {
          step: "5",
          title: "Purification",
          description: "Flash chromatography on silica: EtOAc/Heptane gradient 30 → 70 % EtOAc. Pure MGM-9 fractions pooled, concentrated, recrystallized from EtOH/heptane.",
          solvent: "EtOAc/Heptane → Ethanol/Heptane",
          keyReagents: "Silica, EtOAc, heptane, ethanol"
        }
      ],
      yield: "40–50 % isolated after chromatography (≈ 70 % in-pot LCMS)",
      purityTarget: "≥ 98 % HPLC; ≥ 99 % for reference-standard grade"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Manufacturing Plan (1 kg MGM-9)",
      feasibility: "Moderate — one-pot is scale-friendly but PIFA exotherm management is the critical control.",
      solventsUsed: ["Acetonitrile", "Ethylene glycol (co-solvent, not one of Wohler's standard list — consider THF/ACN swap)", "Ethyl Acetate", "Heptane", "Ethanol"],
      equipment: [
        "200 L jacketed glass-lined reactor with −20 °C cryogenic jacket capacity",
        "Independently controlled addition funnels (2) for solid Selectfluor and PIFA",
        "Inline thermocouple at 3 depths + runaway-trip interlock",
        "Nitrogen blanket + rupture disk",
        "Scrubber train: CaCO₃ scrubber for any HF liberation during workup",
        "100 L extractor vessel with glass-lined liquid-liquid separation",
        "Flash chromatography skid (3 kg silica) or preparative HPLC for final purification",
        "100 L crystallizer with programmable cooling ramp"
      ],
      batchPlan: {
        targetBatch: "1.0 kg MGM-9 (free base) — ≈ 2.4 mol",
        startingMaterial: "1000 g mitragynine oxalate (1.0 eq, 2.0 mol per Wohler MGM9 procedure)",
        keyVolumes: [
          "Acetonitrile: 5.0 L",
          "Ethylene glycol: 5.0 L (co-solvent — under evaluation for replacement with THF)",
          "PIFA: 970 g (1.1 eq)",
          "Selectfluor: 872 g (1.2 eq)",
          "Saturated NaHCO₃: 5.0 L (workup)",
          "Brine: 2.5 L",
          "EtOAc (extraction): 15 L total (3 × 5 L)",
          "Heptane (chromatography + crystallization): 20 L",
          "Ethanol (recrystallization): 4 L"
        ]
      },
      hazards: [
        "PIFA — iodine(III) oxidant; thermal-shock sensitive; store < 25 °C; transfer under N₂; avoid contact with amines at > 30 °C (runaway potential)",
        "Selectfluor — electrophilic fluorinating agent; exothermic addition; moisture-sensitive; avoid contact with reducing agents",
        "Exotherm: ΔT > 30 °C possible on Selectfluor addition — require cryogenic jacket + slow addition (> 20 min) + temperature interlock",
        "HF liberation risk (low but non-zero) from Selectfluor decomposition — CaCO₃ scrubber on vent",
        "Acetonitrile — flammable, toxic by inhalation; ATEX zone 1 compliance",
        "Mitragynine itself is an opioid agonist — handle as Class 2 narcotic for industrial hygiene (no inhalation, no skin contact; OEL ≤ 10 μg/m³)"
      ],
      vendorNotes: [
        "Mitragynine oxalate: Wohler internal production (kratom extract → alkaloid isolation)",
        "PIFA: Sigma-Aldrich, TCI — purchase in ≤ 1 kg containers, store refrigerated",
        "Selectfluor: Sigma-Aldrich (Air Products/Sigma), AK Scientific — purchase in moisture-tight 500-g containers",
        "ACN (HPLC grade): Fisher, Sigma — bulk drum availability",
        "Operator: 2 synthetic chemists + 1 process engineer; minimum PPE: Tyvek suit, full-face respirator with P100, neoprene gloves (PIFA/Selectfluor addition); downgrade to standard lab PPE for chromatography"
      ],
      estCostPerKg: "$45,000 – $68,000 (raw materials + chromatography + utilities); price reflects opioid-grade industrial hygiene overhead",
      standardSolventAlternatives: "Wohler standard solvents compatible: EtOAc (workup), Heptane (chromatography), Ethanol (crystallization). Non-standard: ACN and ethylene glycol — both are necessary per Wohler internal protocol. A process optimization study is recommended to evaluate THF as ethylene glycol replacement."
    },
    safety: {
      acuteToxicity: "No published LD50 — assume mu-opioid opioid-class toxicity pending tox studies. Treat as potentially high-potency opioid for industrial hygiene.",
      chronicConcerns: [
        "Respiratory depression — inherent to MOR agonists (biased signaling reduces but does not eliminate)",
        "Dependence/withdrawal liability",
        "Constipation, urinary retention (μ class effects)",
        "Potential QT prolongation (under evaluation)"
      ],
      contraindications: [
        "Concurrent CNS depressant use (alcohol, benzodiazepines)",
        "Severe respiratory disease",
        "Opioid-naive subjects without appropriate titration",
        "Pregnancy — no data, exclusionary"
      ],
      acuteSideEffects: ["Analgesia", "Sedation", "Nausea/emesis", "Miosis", "Constipation", "Respiratory depression at higher doses"],
      ehsClassification: "GHS Cat. 3 oral (assumed opioid class); industrial hygiene: OEL 10 μg/m³ as acting opioid; containment level requires LEV, closed charging, dedicated cleanroom"
    },
    publishedResearch: [
      { citation: "Acosta S, Yeh V. MGM-9 Scale-Up Campaign Report. Wohler Chemical Internal Document. April 2026." },
      { citation: "Acosta S. MGM-9 One-Pot Synthesis Procedure. Wohler Chemical Internal SOP. 2026." },
      { citation: "Acosta S. MGM-9 Presentation: Synthesis Optimization and Fluorination Methodology. Wohler Chemical. 2026." },
      { citation: "Kruegel AC et al. Synthetic and Receptor Signaling Explorations of the Mitragyna Alkaloids: Mitragynine as an Atypical Molecular Framework for Opioid Receptor Modulators. J Am Chem Soc. 2016;138(21):6754-64." },
      { citation: "Chakraborty S et al. Total Synthesis and Structural Plasticity of Kratom Pseudoindoxyl Metabolites. 2023.", pmid: "37332089" }
    ],
    patentInfo: "Wohler Chemical internal process development — strong candidate for composition-of-matter patent on MGM-9 + method-patents on the one-pot PIFA/Selectfluor process. Prior-art search needed on difluoromitragynine space. Filing recommended before first external distribution.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 23, evidenceQuality: 14, unmetMedicalNeed: 22, novelty: 17, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 14, chronicToxicity: 12, dependence: 12, schedulingRisk: 10 },
      netValue: 38,
      confidenceRationale: "MODERATE — process validated internally; pharmacology data preliminary; scheduling risk real given fluorinated opioid analog scrutiny."
    },
    priorityTier: "Tier 1 — Lead novel compound (but institutional-only)",
    nextSteps: [
      "File provisional patent on MGM-9 composition + one-pot process",
      "Commission formal MOR/KOR/DOR/NOP functional pharmacology at Frontier Sciences or equivalent CRO",
      "Run in vivo antinociception + respiratory-depression comparator study (vs morphine, vs mitragynine)",
      "Draft university partner package (IRB/IACUC-ready)",
      "Finalize ICH-grade crystal form characterization",
      "Conduct legal memo re: federal analog/enforcement risk"
    ]
  },

  "mitragynine": {
    id: "mitragynine",
    name: "Mitragynine",
    fullName: "Mitragynine (primary kratom alkaloid)",
    aliases: ["9-Methoxycorynantheidine"],
    categoryId: "mitragynine",
    program: "Program A",
    cas: "4098-40-2",
    formula: "C₂₃H₃₀N₂O₄",
    molecularWeight: "398.50 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled (DEA). DEA scheduling attempt withdrawn 2016. Several U.S. states have independent scheduling (AL, AR, IN, RI, VT, WI, TN partial). FDA has issued import alerts and warning letters.",
    benefitScore: 80,
    harmScore: 32,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — research-grade standard production",
    summary: "Primary alkaloid of Mitragyna speciosa (kratom) — ~66 % of total alkaloid content. Biased partial MOR agonist: activates G-protein pathways (analgesic, anti-withdrawal) with reduced β-arrestin recruitment vs. morphine. Reduced respiratory depression is a key public-health research angle. Producing standardized research-grade mitragynine fills a real CRO/university supply gap.",
    pros: [
      "Biased MOR agonism — G-protein > β-arrestin = reduced respiratory depression",
      "Used by millions as a de facto harm-reduction tool for OUD and chronic pain",
      "Strong public-health research narrative for fentanyl-harm reduction",
      "Published 2024 Frontiers in Public Health paper supports harm-reduction framing",
      "Wohler Chemical already has kratom processing expertise"
    ],
    cons: [
      "State-level scheduling variability — compliance complexity",
      "Semi-total synthesis complex; practical path is isolation from kratom extract",
      "Dependence potential documented",
      "Public/political controversy around kratom"
    ],
    pharmacology: {
      targets: [
        { receptor: "μ-opioid receptor", activity: "Biased partial agonist (G-protein dominant)", value: "Ki ≈ 100–200 nM; low β-arrestin" },
        { receptor: "κ-opioid receptor", activity: "Partial agonist/antagonist", value: "Mixed activity" },
        { receptor: "δ-opioid receptor", activity: "Partial agonist/antagonist", value: "Mixed" },
        { receptor: "α2-adrenergic", activity: "Weak activity", value: "Contributes to stimulant effects at low dose" }
      ],
      duration: "3–5 h oral",
      halfLife: "≈ 2.5–3.5 h",
      metabolism: "CYP3A4 N-demethylation → 7-hydroxymitragynine (active); CYP2D6 minor",
      mechanism: "Biased MOR partial agonism + multi-receptor modulation explains dose-dependent stimulant (low dose) → opioid-like (high dose) effect profile"
    },
    labSynthesis: {
      title: "Laboratory-Scale Isolation (100–500 g)",
      overview: "Isolation from dried Mitragyna speciosa leaf via acid/base extraction, solvent partition, and silica chromatography.",
      steps: [
        { step: "1", title: "Acid extraction", description: "Dried kratom leaf (1 kg) + 10 % aqueous acetic acid (10 L); stir 2 h at 40 °C; filter.", solvent: "Aqueous acetic acid", keyReagents: "Acetic acid" },
        { step: "2", title: "Basification", description: "Adjust filtrate to pH 9–10 with Na₂CO₃; precipitate total alkaloids.", solvent: "Water", keyReagents: "Na₂CO₃" },
        { step: "3", title: "Solvent extraction", description: "Extract with DCM (or EtOAc for Wohler standard); 3 × 2 L. Combine, wash brine, dry Na₂SO₄, concentrate.", solvent: "Ethyl Acetate (substitute for DCM)", keyReagents: "EtOAc, Na₂SO₄" },
        { step: "4", title: "Silica chromatography", description: "Flash column: EtOAc/MeOH 95:5 → 80:20 gradient. Collect mitragynine-rich fractions (TLC Rf ≈ 0.5 in EtOAc/MeOH 9:1).", solvent: "EtOAc/MeOH", keyReagents: "Silica" },
        { step: "5", title: "Recrystallization", description: "Recrystallize from ethanol/water 4:1 to afford mitragynine ≥ 98 % purity.", solvent: "Ethanol/water", keyReagents: "Ethanol, water" }
      ],
      yield: "12–20 g mitragynine per kg dried leaf (depends on source material)",
      purityTarget: "≥ 98.0 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Isolation Plan (1 kg mitragynine)",
      feasibility: "High — isolation process is Wohler's existing core competency.",
      solventsUsed: ["Ethyl Acetate (replacing DCM)", "Ethanol", "Methanol (chromatography)", "Heptane"],
      equipment: [
        "500 L stainless-steel extraction vessel with steam jacket (acid extraction)",
        "Filter press + decanter (biomass removal)",
        "200 L pH-controlled basification tank",
        "200 L liquid-liquid extractor",
        "100 L rotary evaporator",
        "Preparative flash chromatography skid (20 kg silica capacity) or simulated moving bed (SMB) chromatography for economic production",
        "100 L crystallizer"
      ],
      batchPlan: {
        targetBatch: "1.0 kg mitragynine (free base)",
        startingMaterial: "65–85 kg dried kratom leaf (depends on alkaloid content)",
        keyVolumes: [
          "10 % acetic acid extraction: 650 L",
          "Na₂CO₃ for basification: 25 kg",
          "EtOAc extraction: 450 L total",
          "EtOH (chromatography + crystallization): 80 L",
          "MeOH (chromatography elution): 40 L",
          "Heptane (wash): 20 L"
        ]
      },
      hazards: [
        "Kratom biomass dust — respiratory hazard during handling (LEV required)",
        "Mitragynine itself is a MOR agonist — industrial hygiene controls",
        "EtOAc flammable — ATEX zone 1",
        "Extraction exotherm on acid addition — jacket cooling required"
      ],
      vendorNotes: [
        "Kratom leaf: Wohler supply chain (direct-source Indonesian Mitragyna speciosa)",
        "EtOAc, EtOH, MeOH: bulk tanker delivery",
        "Silica: Fisher, Sigma (60-Å, 40–63 μm)"
      ],
      estCostPerKg: "$4,500 – $8,000 (materials + chromatography) for ≥ 98 % grade"
    },
    safety: {
      acuteToxicity: "Mitragynine rat LD50 ≈ 547 mg/kg oral; human fatalities rare and almost always polydrug.",
      chronicConcerns: ["Dependence with heavy daily use", "Hepatotoxicity (rare, idiosyncratic)", "Withdrawal syndrome (opioid-like but typically milder)"],
      contraindications: ["Concurrent opioids, alcohol, benzodiazepines", "Pregnancy (risk of neonatal abstinence)"],
      acuteSideEffects: ["Nausea", "Mild analgesia/sedation at high dose; stimulation at low dose", "Constipation", "Mild hypertension"],
      ehsClassification: "GHS Cat. 4 oral; biomass dust inhalation hazard; industrial hygiene for MOR agonist"
    },
    publishedResearch: [
      { citation: "Kratom as a potential substance use disorder harm reduction agent. Front Public Health. 2024.", pmc: "PMC11169875" },
      { citation: "Kruegel AC et al. JACS 2016;138:6754-6764." },
      { citation: "WashU Medicine. Kratom Alkaloids as Probes for Opioid Receptor Function. 2022-2024." }
    ],
    patentInfo: "Natural product composition — no COM patents. Isolation/purification methods, USP-grade reference standards, and formulations are patentable.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 18, unmetMedicalNeed: 22, novelty: 8, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 6, chronicToxicity: 10, dependence: 10, schedulingRisk: 6 },
      netValue: 48,
      confidenceRationale: "HIGH — extensive literature; Wohler existing processing capability"
    },
    priorityTier: "Tier 1 — Near-term revenue generator",
    nextSteps: [
      "Produce 5 kg USP-grade reference standard",
      "Issue COA with NMR, HRMS, HPLC, residual solvents, and chiral purity",
      "Establish CRO distribution pipeline with 5+ university customers",
      "File method patent on SMB chromatographic isolation process"
    ]
  },

  "mitragynine-pseudoindoxyl": {
    id: "mitragynine-pseudoindoxyl",
    name: "Mitragynine Pseudoindoxyl (MGP)",
    fullName: "Mitragynine pseudoindoxyl — 9-hydroxycorynantheidine pseudoindoxyl",
    aliases: ["MGP", "Mitragynine Pseudoindoxyl"],
    categoryId: "mitragynine",
    program: "Program A / D",
    formula: "C₂₃H₃₀N₂O₅",
    molecularWeight: "414.50 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled as of 2026. Very high scrutiny — potent MOR ligand derived from kratom metabolism. Institutional-only posture mandatory.",
    benefitScore: 90,
    harmScore: 45,
    evidenceConfidence: "HIGH",
    status: "Hold — Institutional Only",
    statusColor: "orange",
    goDecision: "HOLD / Conditional GO — with university partner + legal memo",
    summary: "A metabolic product of mitragynine and 7-hydroxymitragynine. High MOR affinity; biased signaling with reduced β-arrestin recruitment. ≈ 20× more potent than morphine in antinociceptive assays with substantially reduced respiratory depression in mouse models. Complete synthesis route published in 2023 (Chakraborty et al.). Potentially the highest-impact single compound in the kratom space.",
    pros: [
      "≈ 20× more potent than morphine with reduced respiratory depression in preclinical models",
      "Natural-product origin — from kratom metabolism",
      "Complete total synthesis route published 2023",
      "Addresses the single biggest unmet need in pain/OUD research",
      "Publishable, patentable method/formulation space"
    ],
    cons: [
      "Very high enforcement/analog scrutiny — potent MOR agonist",
      "Synthesis complexity — multi-step total synthesis required",
      "Limited in vivo safety data at scale"
    ],
    pharmacology: {
      targets: [
        { receptor: "μ-opioid receptor", activity: "Biased agonist (G-protein dominant)", value: "Ki ≈ 0.8 nM" },
        { receptor: "δ-opioid receptor", activity: "Antagonist", value: "Contributes to reduced tolerance profile" }
      ],
      duration: "Estimated 4–6 h in rodent models",
      halfLife: "Under characterization",
      metabolism: "Extensive hepatic metabolism; minor urinary excretion of parent",
      mechanism: "Potent biased MOR agonist + DOR antagonist = theoretically analgesia with reduced tolerance"
    },
    labSynthesis: {
      title: "Laboratory-Scale Total Synthesis",
      overview: "Published 2023 total synthesis (Chakraborty et al., PMID 37332089). Multi-step synthesis from tryptamine starting material through Pictet-Spengler cyclization, stereoselective lactamization, and final oxidative rearrangement to the pseudoindoxyl scaffold.",
      steps: [
        { step: "1", title: "Pictet-Spengler cyclization", description: "Tryptamine + aldehyde in acidic conditions → tetrahydro-β-carboline intermediate.", solvent: "TFA/DCM (substitute ACN/MeOH for Wohler)", keyReagents: "TFA, aldehyde" },
        { step: "2", title: "Stereoselective lactamization", description: "Amide formation and ring closure under chiral control.", solvent: "THF", keyReagents: "Coupling reagents" },
        { step: "3", title: "Oxidative rearrangement", description: "Oxidation of indole to pseudoindoxyl (similar PIFA-type chemistry as MGM-9).", solvent: "ACN/ethylene glycol", keyReagents: "PIFA or equivalent" },
        { step: "4", title: "Final purification", description: "Flash chromatography EtOAc/heptane gradient, then recrystallization from ethanol.", solvent: "EtOAc/Heptane/EtOH", keyReagents: "Silica" }
      ],
      yield: "15–25 % over 6–8 steps (published route)",
      purityTarget: "≥ 98 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Low-Moderate — synthesis complexity limits practical scale-up; 100-g to 1-kg is realistic near-term target.",
      solventsUsed: ["Acetonitrile", "THF", "Ethyl Acetate", "Heptane", "Ethanol", "Methanol"],
      equipment: ["Multi-stage reactor train", "Preparative HPLC for intermediate purification", "Large-scale crystallizer"],
      batchPlan: {
        targetBatch: "100 g MGP (realistic first industrial target)",
        startingMaterial: "≈ 300 g tryptamine or mitragynine-derived intermediate",
        keyVolumes: ["Step-specific — see published 2023 synthesis supplementary materials"]
      },
      hazards: ["Multi-step opioid synthesis — containment throughout", "PIFA in oxidation step — same as MGM-9"],
      vendorNotes: ["Published route uses specialized reagents; Wohler will need to establish process development program"],
      estCostPerKg: "$150,000+ at 100-g scale; $40,000–$80,000 at 1-kg with optimization"
    },
    safety: {
      acuteToxicity: "No published LD50. Potent MOR agonist — treat as high-potency opioid.",
      chronicConcerns: ["Respiratory depression (reduced vs morphine but non-zero)", "Dependence", "DOR antagonism may reduce tolerance but needs validation"],
      contraindications: ["Same as MGM-9 — opioid-naive, concurrent CNS depressants, pregnancy"],
      acuteSideEffects: ["Potent analgesia", "Sedation", "Constipation", "Reduced but present respiratory depression"],
      ehsClassification: "Highest industrial hygiene tier — OEL < 1 μg/m³"
    },
    publishedResearch: [
      { citation: "Chakraborty S et al. Total Synthesis and Structural Plasticity of Kratom Pseudoindoxyl Metabolites. 2023.", pmid: "37332089" },
      { citation: "Váradi A et al. Mitragynine/Corynantheidine Pseudoindoxyls As Opioid Analgesics with Mu Agonism and Delta Antagonism. J Med Chem. 2016;59(18):8381-97." }
    ],
    patentInfo: "Columbia University and others hold some method patents; composition-of-matter patents have been filed in adjacent space. Wohler process-development patents may be available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 25, evidenceQuality: 19, unmetMedicalNeed: 23, novelty: 15, universityInterest: 8 },
      harmBreakdown: { acuteToxicity: 13, chronicToxicity: 12, dependence: 12, schedulingRisk: 8 },
      netValue: 45,
      confidenceRationale: "HIGH — published total synthesis; strong pharmacology rationale."
    },
    priorityTier: "Tier 1 — Highest-impact aspirational",
    nextSteps: [
      "Literature-review of 2023 synthesis supplementary materials",
      "Partner with university lab (Kruegel/Majumdar collaborators) for first 1-g synthesis",
      "File process improvement patents",
      "Proceed only with IRB-registered research partner in place"
    ]
  },

  "speciociliatine": {
    id: "speciociliatine",
    name: "Speciociliatine",
    fullName: "Speciociliatine — (stereoisomer of mitragynine)",
    aliases: [],
    categoryId: "mitragynine",
    program: "Program A",
    formula: "C₂₃H₃₀N₂O₄",
    molecularWeight: "398.50 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 75,
    harmScore: 30,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — excellent comparative research tool",
    summary: "Minor kratom alkaloid (~1 % content) and a stereoisomer of mitragynine. WashU research identifies speciociliatine as a MORE mu-selective opioid agonist than mitragynine — and notably less functionally biased. The mitragynine-vs-speciociliatine comparison is an ideal scientific tool for studying the relationship between functional bias and respiratory safety.",
    pros: [
      "Direct scientific comparator to mitragynine for bias-vs-safety research",
      "Higher MOR selectivity — cleaner pharmacology",
      "Natural product — strong research narrative",
      "Emerging WashU/academic interest creates publication pull"
    ],
    cons: ["Low natural abundance (~1 %) — semi-synthesis via epimerization may be needed", "Less biased = potentially higher respiratory depression than mitragynine"],
    pharmacology: {
      targets: [
        { receptor: "μ-opioid", activity: "Selective full agonist (less biased than mitragynine)", value: "Higher MOR selectivity; more conventional signaling" },
        { receptor: "κ-opioid", activity: "Weak", value: "Minimal" }
      ],
      duration: "Estimated similar to mitragynine",
      metabolism: "Similar to mitragynine",
      mechanism: "Conventional MOR agonism — research comparator to mitragynine's biased profile"
    },
    labSynthesis: {
      title: "Laboratory-Scale Production",
      overview: "Two routes: (A) isolation from kratom biomass + extensive chromatography; (B) epimerization of mitragynine under controlled acidic conditions.",
      steps: [
        { step: "1", title: "Mitragynine starting material", description: "Begin with Wohler-produced mitragynine (≥ 98 %).", solvent: "N/A", keyReagents: "Mitragynine" },
        { step: "2", title: "Acid-catalyzed epimerization", description: "Dissolve mitragynine in EtOH; add catalytic HCl; reflux to establish thermodynamic mixture. Typical 60:40 mitragynine:speciociliatine at equilibrium.", solvent: "Ethanol", keyReagents: "HCl (catalytic)" },
        { step: "3", title: "Chromatographic separation", description: "Silica column: EtOAc/MeOH 9:1 with NH₄OH modifier. Speciociliatine elutes after mitragynine.", solvent: "EtOAc/MeOH/NH₄OH", keyReagents: "Silica" },
        { step: "4", title: "Crystallization", description: "Crystallize speciociliatine from ethanol/heptane.", solvent: "Ethanol/Heptane", keyReagents: "Ethanol, heptane" }
      ],
      yield: "30–40 % of starting mitragynine (equilibrium-limited)",
      purityTarget: "≥ 98 % HPLC (diastereomeric purity critical)"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — requires robust chromatographic separation.",
      solventsUsed: ["Ethanol", "Ethyl Acetate", "Methanol", "Heptane"],
      equipment: ["Simulated moving bed (SMB) chromatography highly recommended for diastereomer separation at kg scale"],
      batchPlan: {
        targetBatch: "1.0 kg speciociliatine",
        startingMaterial: "3.0–3.5 kg mitragynine",
        keyVolumes: ["Ethanol (epimerization): 15 L", "EtOAc/MeOH chromatography: 40 L total", "Heptane crystallization: 5 L"]
      },
      hazards: ["Standard opioid industrial hygiene; no novel hazards vs mitragynine"],
      vendorNotes: ["Mitragynine: Wohler internal", "SMB equipment: Novasep, LudgerPharma"],
      estCostPerKg: "$15,000 – $22,000"
    },
    safety: {
      acuteToxicity: "No published LD50; assume mitragynine-range",
      chronicConcerns: ["Higher conventional signaling than mitragynine → potentially more respiratory depression in chronic dosing"],
      contraindications: ["Opioid contraindications"],
      acuteSideEffects: ["Mitragynine-like but more opioid-classic"],
      ehsClassification: "GHS Cat. 4 oral; MOR-agonist industrial hygiene"
    },
    publishedResearch: [
      { citation: "WashU Medicine. Kratom Alkaloids as Probes for Opioid Receptor Function. 2022." },
      { citation: "Kruegel AC, Grundmann O. The medicinal chemistry and neuropharmacology of kratom: A preliminary discussion. Neuropharmacology. 2018;134:108-120." }
    ],
    patentInfo: "Composition limited; separation method and research-reference-standard patents available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 19, evidenceQuality: 16, unmetMedicalNeed: 17, novelty: 13, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 8, chronicToxicity: 10, dependence: 8, schedulingRisk: 4 },
      netValue: 45,
      confidenceRationale: "MODERATE — clear scientific comparison value; limited clinical data."
    },
    priorityTier: "Tier 2",
    nextSteps: ["Produce 100-g reference standard", "Publish comparison study with WashU collaborators"]
  },

  "speciogynine": {
    id: "speciogynine",
    name: "Speciogynine",
    fullName: "Speciogynine",
    aliases: [],
    categoryId: "mitragynine",
    program: "Program A",
    formula: "C₂₃H₃₀N₂O₄",
    molecularWeight: "398.50 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 72,
    harmScore: 25,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — kratom pharmacology deconvolution tool",
    summary: "Minor kratom alkaloid (~7 % content). Mu-opioid antagonist with partial agonism at kappa and delta opioid receptors. Functions as a multi-receptor opioid modulator. May contribute to kratom's 'ceiling effect' — a key property underlying harm-reduction claims.",
    pros: [
      "Multi-receptor opioid modulation — scientifically unique research probe",
      "Contributes to kratom's characteristic ceiling effect (harm-reduction property)",
      "Natural kratom alkaloid at meaningful abundance (~7 %)",
      "Lower abuse liability than full MOR agonists"
    ],
    cons: ["Moderate natural abundance limits isolation yield", "Complex pharmacology requires careful assay design"],
    pharmacology: {
      targets: [
        { receptor: "μ-opioid", activity: "Antagonist", value: "Blocks MOR agonism" },
        { receptor: "κ-opioid", activity: "Partial agonist", value: "Contributes to anti-withdrawal effects" },
        { receptor: "δ-opioid", activity: "Partial agonist", value: "Minor contribution" }
      ],
      duration: "Estimated 4–6 h",
      metabolism: "Hepatic CYP metabolism analogous to mitragynine",
      mechanism: "Complex opioid modulation — mu antagonism + kappa/delta partial agonism creates ceiling effect on mu signaling"
    },
    labSynthesis: {
      title: "Laboratory-Scale Isolation",
      overview: "Direct isolation from kratom biomass; chromatographic separation from mitragynine.",
      steps: [
        { step: "1", title: "Total alkaloid extraction", description: "Same acid/base extraction as mitragynine isolation.", solvent: "Aqueous acid → EtOAc", keyReagents: "Acetic acid, Na₂CO₃, EtOAc" },
        { step: "2", title: "Silica chromatography", description: "EtOAc/MeOH gradient; speciogynine elutes at distinct Rf from mitragynine.", solvent: "EtOAc/MeOH", keyReagents: "Silica" },
        { step: "3", title: "Recrystallization", description: "Ethanol/water.", solvent: "Ethanol/water", keyReagents: "Ethanol, water" }
      ],
      yield: "1.5–3 g per kg dried leaf",
      purityTarget: "≥ 98 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Low — limited by biomass; 100-g scale is realistic.",
      solventsUsed: ["Ethyl Acetate", "Methanol", "Ethanol", "Heptane"],
      equipment: ["SMB chromatography for economic separation"],
      batchPlan: { targetBatch: "100 g speciogynine", startingMaterial: "35–60 kg kratom leaf (depends on source alkaloid ratios)", keyVolumes: ["Matched to 1 kg mitragynine isolation"] },
      hazards: ["Standard biomass + opioid handling"],
      vendorNotes: ["Wohler internal supply"],
      estCostPerKg: "$35,000 – $55,000 (limited by natural abundance)"
    },
    safety: {
      acuteToxicity: "Limited data; assume mitragynine-range safety",
      chronicConcerns: ["Unknown long-term effects of kappa/delta balance"],
      contraindications: ["Concurrent opioid therapy (will antagonize)", "Pregnancy"],
      acuteSideEffects: ["Dysphoria possible (kappa component)", "No analgesia from mu", "May precipitate withdrawal in opioid-dependent subjects"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [
      { citation: "WashU Medicine. Kratom Alkaloids as Opioid Receptor Probes. 2022." },
      { citation: "Chear NJ et al. Exploring the Chemistry of Alkaloids from Malaysian Mitragyna speciosa. Molecules. 2021;26(4):1109." }
    ],
    patentInfo: "Natural product — composition not patentable; isolation/formulation methods available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 15, unmetMedicalNeed: 15, novelty: 14, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 7, dependence: 4, schedulingRisk: 9 },
      netValue: 47,
      confidenceRationale: "MODERATE — interesting pharmacology, limited pool of data"
    },
    priorityTier: "Tier 2",
    nextSteps: ["100-g reference standard", "WashU comparative receptor-function study"]
  }
};